import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, FlatList, ActivityIndicator, StyleSheet, TextInput, Platform, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { InsetModal as Modal } from './InsetModal';
import { apiService } from '../services/api';
import { useLocale } from '../contexts/LocaleContext';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { colors, spacing, typography } from '../theme';
import { getCurrencySymbol } from '../utils/currencies';

export type ShareDocumentType = 'quotation' | 'order';
type Mode = 'details' | 'pdf' | 'both';

/**
 * Send one of this customer's quotations or orders into the chat: its details, its PDF
 * (quotation PDF / order invoice), or both — on WhatsApp optionally with buttons
 * (Accept / Request changes, or Confirm / Cancel for a pending order).
 */
export function ShareDocumentModal({ visible, type, conversationId, contactId, channel, language, onClose, onSent, onCreateNew }: {
  visible: boolean; type: ShareDocumentType; conversationId: string; contactId?: string | null;
  channel?: string; language: 'ar' | 'en';
  onClose: () => void; onSent: () => void; onCreateNew: () => void;
}) {
  const styles = useThemedStyles(getStyles);
  const { t, isRTL } = useLocale();
  const [rows, setRows] = useState<any[] | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<any | null>(null);
  const [mode, setMode] = useState<Mode>('both');
  const [buttons, setButtons] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const { width } = useWindowDimensions();
  // Web: cover the conversation pane only (like the template picker), not the whole window.
  const inPane = Platform.OS === 'web' && width >= 768;
  const whatsapp = ['whatsapp', 'wabu'].includes(channel || '');
  const row = { flexDirection: isRTL ? 'row-reverse' as const : 'row' as const };
  const align = { textAlign: isRTL ? 'right' as const : 'left' as const };

  useEffect(() => {
    if (!visible) return;
    setSelected(null); setError(''); setMode(channel === 'x-twitter' ? 'details' : 'both'); setButtons(true);
  }, [visible, type]);

  // This customer's records, newest first; search narrows by number, name or phone.
  useEffect(() => {
    if (!visible) return;
    let active = true;
    setRows(null);
    const timer = setTimeout(() => {
      const load = type === 'quotation'
        ? apiService.getQuotations({ contactId: contactId || undefined, search: search || undefined, limit: 30 }).then(r => r.data?.quotations || [])
        : apiService.getOrders({ contactId: contactId || undefined, search: search || undefined, limit: 30 } as any).then(r => Array.isArray(r.data) ? r.data : (r.data as any)?.orders || []);
      void load.then(list => { if (active) setRows(list); }).catch(() => { if (active) setRows([]); });
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [visible, type, contactId, search]);

  const number = (r: any) => type === 'quotation' ? r.quotationNumber : r.orderNumber;
  const status = (r: any) => type === 'quotation' ? r.quotationStatus : r.orderStatus;
  const canButton = (r: any) => whatsapp && (type === 'quotation' ? ['draft', 'sent'].includes(r.quotationStatus) : ['pending', 'placed'].includes(r.orderStatus));

  const send = async () => {
    if (!selected) return;
    setSending(true); setError('');
    try {
      const response = await apiService.shareConversationDocument(conversationId, {
        type, id: String(selected._id), mode, buttons: buttons && canButton(selected) && mode !== 'pdf', language,
      });
      if (response.statusCode !== 1000) throw new Error(response.errors?.map((e: any) => typeof e === 'string' ? e : e.message).join(' ') || t('share.failed'));
      onSent(); onClose();
    } catch (err: any) { setError(err.message || t('share.failed')); }
    finally { setSending(false); }
  };

  const option = (value: Mode, label: string, icon: string, disabled = false) => (
    <TouchableOpacity key={value} disabled={disabled} accessibilityRole="radio" accessibilityState={{ selected: mode === value, disabled }}
      style={[styles.option, row, mode === value && styles.optionActive, disabled && styles.disabled]} onPress={() => setMode(value)}>
      <Ionicons name={icon as any} size={16} color={mode === value ? (colors.white as string) : (colors.primary as string)} />
      <Text style={[styles.optionText, mode === value && styles.optionTextActive]}>{label}</Text>
    </TouchableOpacity>
  );

  const page = (
      <View style={styles.page}>
        <View style={[styles.header, row]}>
          <TouchableOpacity onPress={selected ? () => setSelected(null) : onClose} accessibilityRole="button" accessibilityLabel={t('common.back')}>
            <Ionicons name={isRTL ? 'arrow-forward' : 'arrow-back'} size={22} color={colors.text.primary as string} />
          </TouchableOpacity>
          <Text style={styles.title}>{t(type === 'quotation' ? 'share.sendQuotation' : 'share.sendOrder')}</Text>
          <TouchableOpacity onPress={() => { onClose(); onCreateNew(); }} accessibilityRole="button">
            <Text style={styles.link}>{t(type === 'quotation' ? 'share.newQuotation' : 'share.newOrder')}</Text>
          </TouchableOpacity>
        </View>

        {!selected ? <>
          <View style={[styles.search, row]}>
            <Ionicons name="search" size={16} color={colors.text.secondary as string} />
            <TextInput value={search} onChangeText={setSearch} placeholder={t('share.search')} placeholderTextColor={colors.text.secondary as string} style={[styles.searchInput, align]} />
          </View>
          {!contactId && <Text style={[styles.hint, align]}>{t('share.noContact')}</Text>}
          {rows === null ? <ActivityIndicator style={{ marginTop: spacing.lg }} color={colors.primary} /> : (
            <FlatList data={rows} keyExtractor={r => String(r._id)} contentContainerStyle={{ padding: spacing.md, gap: spacing.sm }}
              ListEmptyComponent={<Text style={[styles.hint, align]}>{t(type === 'quotation' ? 'share.noQuotations' : 'share.noOrders')}</Text>}
              renderItem={({ item }) => (
                <TouchableOpacity accessibilityRole="button" style={[styles.card, row]} onPress={() => { setSelected(item); setButtons(true); }}>
                  <View style={{ flex: 1, gap: 2 }}>
                    <Text style={[styles.cardTitle, align]}>{number(item)}</Text>
                    <Text style={[styles.hint, align]}>{new Date(item.createdAt || item.orderDate).toLocaleDateString()} · {status(item)}</Text>
                  </View>
                  <Text style={styles.cardTitle}>{getCurrencySymbol(item.currency)} {Number(item.totalPrice || 0).toFixed(2)}</Text>
                </TouchableOpacity>
              )} />
          )}
        </> : <View style={{ padding: spacing.md, gap: spacing.md }}>
          <View style={[styles.card, row]}>
            <View style={{ flex: 1, gap: 2 }}>
              <Text style={[styles.cardTitle, align]}>{number(selected)}</Text>
              <Text style={[styles.hint, align]}>{status(selected)} · {(selected.items || []).length} {t('share.items')}</Text>
            </View>
            <Text style={styles.cardTitle}>{getCurrencySymbol(selected.currency)} {Number(selected.totalPrice || 0).toFixed(2)}</Text>
          </View>
          <Text style={[styles.label, align]}>{t('share.what')}</Text>
          <View style={[styles.options, row]}>
            {option('details', t('share.details'), 'list-outline')}
            {option('pdf', t('share.pdf'), 'document-attach-outline', channel === 'x-twitter')}
            {option('both', t('share.both'), 'documents-outline', channel === 'x-twitter')}
          </View>
          {canButton(selected) && mode !== 'pdf' && <TouchableOpacity accessibilityRole="checkbox" accessibilityState={{ checked: buttons }} style={[styles.check, row]} onPress={() => setButtons(v => !v)}>
            <Ionicons name={buttons ? 'checkbox' : 'square-outline'} size={20} color={colors.primary as string} />
            <Text style={[styles.body, align, { flex: 1 }]}>{t(type === 'quotation' ? 'share.quotationButtons' : 'share.orderButtons')}</Text>
          </TouchableOpacity>}
          {!!error && <Text accessibilityRole="alert" style={[styles.error, align]}>{error}</Text>}
          <TouchableOpacity accessibilityRole="button" disabled={sending} style={[styles.send, sending && styles.disabled]} onPress={() => void send()}>
            {sending ? <ActivityIndicator color="white" /> : <Text style={styles.sendText}>{t('share.send')}</Text>}
          </TouchableOpacity>
        </View>}
      </View>
  );
  if (inPane) return visible ? <View style={styles.pane}>{page}</View> : null;
  return <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>{page}</Modal>;
}

const getStyles = () => StyleSheet.create({
  page: { flex: 1, backgroundColor: colors.background.primary },
  pane: { ...StyleSheet.absoluteFillObject, zIndex: 100 } as any,
  header: { alignItems: 'center', justifyContent: 'space-between', padding: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border, gap: spacing.md },
  title: { flex: 1, fontSize: typography.fontSize.base, fontWeight: '600', color: colors.text.primary },
  link: { color: colors.primary, fontWeight: '600' },
  search: { alignItems: 'center', gap: spacing.sm, margin: spacing.md, paddingHorizontal: spacing.md, borderWidth: 1, borderColor: colors.border, borderRadius: 10 },
  searchInput: { flex: 1, paddingVertical: spacing.sm, color: colors.text.primary },
  card: { alignItems: 'center', gap: spacing.md, padding: spacing.md, borderWidth: 1, borderColor: colors.border, borderRadius: 12, backgroundColor: colors.background.surface },
  cardTitle: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.text.primary },
  hint: { fontSize: typography.fontSize.xs, color: colors.text.secondary, paddingHorizontal: spacing.md },
  label: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.text.primary },
  body: { fontSize: typography.fontSize.sm, color: colors.text.primary },
  options: { flexWrap: 'wrap', gap: spacing.sm },
  option: { alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, borderRadius: 20, borderWidth: 1, borderColor: colors.primary },
  optionActive: { backgroundColor: colors.primary },
  optionText: { color: colors.primary, fontWeight: '600' },
  optionTextActive: { color: colors.white },
  check: { alignItems: 'center', gap: spacing.sm },
  error: { color: colors.error },
  send: { backgroundColor: colors.primary, padding: spacing.md, borderRadius: 10, alignItems: 'center' },
  sendText: { color: 'white', fontWeight: '600' },
  disabled: { opacity: 0.5 },
});
